/**
 * eCommerce and custom event tracking functions (GA4 + PostHog + Braze).
 *
 * Most functions map to standard GA4 eCommerce events. Custom events
 * (e.g. start_consultation) are documented individually below.
 * All functions also fire the equivalent PostHog event so both platforms
 * receive identical funnel data, plus a Braze custom event for marketing
 * automation — Braze only fires when marketing (advertising) consent is
 * granted (see `src/lib/analytics/braze.ts`).
 *
 * GA4 reference:
 * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 *
 * All functions are safe to call on the server (no-op) or before
 * the GTM dataLayer/PostHog/Braze loads.
 *
 * **Per-sink consent posture**
 *
 * GA4 dataLayer: product-identifying fields (`item_id`, `item_name`, etc.)
 * are scrubbed via `scrubItemsForDataLayer` when analytics (Statistics)
 * consent is absent. In Advanced Consent Mode GA still sends cookieless event
 * pings, so item names must not be present without consent.
 *
 * PostHog receives FULL items and self-gates: it opts out entirely when
 * analytics consent is denied, so nothing leaks then anyway.
 *
 * Braze receives FULL items and self-gates: it no-ops without marketing
 * (advertising) consent. With marketing consent the user has accepted
 * health-data-in-marketing.
 */

import { brazeCapture } from './braze';
import { getConsentPreferences, hasAnalyticsConsent } from './consent';
import { pushDataLayer, pushEcommerce } from './datalayer';
import { scrubItemsForDataLayer } from './items';
import { posthogCapture } from './posthog';
import type { EnhancedConversionsUserData, GA4Item } from './types';

/** Check whether at least one field in userData is non-empty. */
const hasUserData = (data: EnhancedConversionsUserData): boolean =>
  Object.values(data).some(Boolean);

/** view_item_list — product listing / category / collection pages */
export const trackViewItemList = (
  items: GA4Item[],
  listId?: string,
  listName?: string,
): void => {
  if (items.length === 0) {
    return;
  }

  const analytics = hasAnalyticsConsent();

  const params = {
    item_list_id: analytics ? listId : undefined,
    item_list_name: analytics ? listName : undefined,
    items,
  };

  pushEcommerce('view_item_list', {
    ...params,
    items: scrubItemsForDataLayer(items),
  });
  posthogCapture('view_item_list', params);
  brazeCapture('view_item_list', params);
};

/** view_item — product detail page */
export const trackViewItem = (
  item: GA4Item,
  value: number,
  currency: string,
): void => {
  const params = {
    currency,
    value,
    items: [item],
  };

  pushEcommerce('view_item', {
    ...params,
    items: scrubItemsForDataLayer([item]),
  });
  posthogCapture('view_item', params);
  brazeCapture('view_item', params);
};

/** add_to_cart */
export const trackAddToCart = (
  item: GA4Item,
  value: number,
  currency: string,
): void => {
  const params = {
    currency,
    value,
    items: [item],
  };

  pushEcommerce('add_to_cart', {
    ...params,
    items: scrubItemsForDataLayer([item]),
  });
  posthogCapture('add_to_cart', params);
  brazeCapture('add_to_cart', params);
};

/** remove_from_cart */
export const trackRemoveFromCart = (
  item: GA4Item,
  value: number,
  currency: string,
): void => {
  const params = {
    currency,
    value,
    items: [item],
  };

  pushEcommerce('remove_from_cart', {
    ...params,
    items: scrubItemsForDataLayer([item]),
  });
  posthogCapture('remove_from_cart', params);
  brazeCapture('remove_from_cart', params);
};

/** view_cart */
export const trackViewCart = (
  items: GA4Item[],
  value: number,
  currency: string,
): void => {
  const params = {
    currency,
    value,
    items,
  };

  pushEcommerce('view_cart', {
    ...params,
    items: scrubItemsForDataLayer(items),
  });
  posthogCapture('view_cart', params);
  brazeCapture('view_cart', params);
};

/** begin_checkout */
export const trackBeginCheckout = (
  items: GA4Item[],
  value: number,
  currency: string,
): void => {
  const params = {
    currency,
    value,
    items,
  };

  pushEcommerce('begin_checkout', {
    ...params,
    items: scrubItemsForDataLayer(items),
  });
  posthogCapture('begin_checkout', params);
  brazeCapture('begin_checkout', params);
};

/** add_shipping_info */
export const trackAddShippingInfo = (
  items: GA4Item[],
  value: number,
  currency: string,
  shippingTier?: string,
): void => {
  const params = {
    currency,
    value,
    shipping_tier: shippingTier,
    items,
  };

  pushEcommerce('add_shipping_info', {
    ...params,
    items: scrubItemsForDataLayer(items),
  });
  posthogCapture('add_shipping_info', params);
  brazeCapture('add_shipping_info', params);
};

/** add_payment_info */
export const trackAddPaymentInfo = (
  items: GA4Item[],
  value: number,
  currency: string,
  paymentType?: string,
): void => {
  const params = {
    currency,
    value,
    payment_type: paymentType,
    items,
  };

  pushEcommerce('add_payment_info', {
    ...params,
    items: scrubItemsForDataLayer(items),
  });
  posthogCapture('add_payment_info', params);
  brazeCapture('add_payment_info', params);
};

/**
 * start_consultation — user clicks "Start consultation" on the PDP for a P/POM product.
 *
 * This is a custom event (not a standard GA4 eCommerce event) that tracks when
 * a user initiates the consultation flow required before purchasing pharmacy
 * medicines. Combined with add_to_cart (which fires on consultation completion),
 * this enables a "PDP Primary Action Rate" metric for P/POM products.
 */
export const trackStartConsultation = (
  item: GA4Item,
  value: number,
  currency: string,
  legalClassification: string,
  questionnaireId?: string,
): void => {
  const params = {
    currency,
    value,
    legal_classification: legalClassification,
    questionnaire_id: questionnaireId,
    items: [item],
  };
  pushEcommerce(
    'start_consultation',
    { currency, value, items: scrubItemsForDataLayer([item]) },
    {
      legal_classification: legalClassification,
      questionnaire_id: questionnaireId,
    },
  );
  posthogCapture('start_consultation', params);
  brazeCapture('start_consultation', params);
};

/**
 * complete_consultation — fires when the user successfully completes a consultation.
 */
export const trackCompleteConsultation = (
  item: GA4Item,
  value: number,
  currency: string,
  questionnaireId: string,
  submissionId: string,
): void => {
  const params = {
    currency,
    value,
    questionnaire_id: questionnaireId,
    submission_id: submissionId,
    items: [item],
  };
  pushEcommerce(
    'complete_consultation',
    { currency, value, items: scrubItemsForDataLayer([item]) },
    { questionnaire_id: questionnaireId, submission_id: submissionId },
  );
  posthogCapture('complete_consultation', params);
  brazeCapture('complete_consultation', params);
};

// =====================================================================
// P med consultation funnel events
//
// PostHog self-gates (opts out without analytics consent).
// Braze self-gates (no-ops without marketing consent).
//
// Per-sink rule: Braze (a marketing profile) only receives the commerce
// lifecycle — started / approved / cart_added, mirroring
// start_consultation / complete_consultation / add_to_cart. Per-question
// progress and rejection outcomes are medical detail and are PostHog-only;
// consent gating alone is NOT sufficient to send them to a marketing sink.
// =====================================================================

/** p_med_consultation_started — fires when the modal opens and session starts */
export const trackPMedConsultationStarted = (payload: {
  product_id: string;
  variant_id: string;
  questionnaire_id: string;
  questionnaire_version?: string;
}): void => {
  posthogCapture('p_med_consultation_started', payload);
  brazeCapture('p_med_consultation_started', payload);
};

/** p_med_consultation_question_viewed — fires whenever a new question becomes current */
export const trackPMedConsultationQuestionViewed = (payload: {
  question_id: string;
  index: number;
  visible_total: number;
}): void => {
  posthogCapture('p_med_consultation_question_viewed', payload);
};

/** p_med_consultation_question_answered — fires when the user answers and advances */
export const trackPMedConsultationQuestionAnswered = (payload: {
  question_id: string;
  index: number;
  visible_total: number;
  duration_ms: number;
}): void => {
  posthogCapture('p_med_consultation_question_answered', payload);
};

/**
 * p_med_consultation_rejected — fires on rejection from Formulate
 * (answer_actions or submit).
 *
 * Carries no rejection text: the Formulate reason message is medical
 * content and must never reach an analytics or marketing sink. `stage`
 * distinguishes an inline answer rejection from a submit-time block;
 * `question_id` adds no exposure beyond question_viewed/answered.
 */
export const trackPMedConsultationRejected = (payload: {
  stage: 'answer' | 'submit';
  question_id?: string;
}): void => {
  posthogCapture('p_med_consultation_rejected', payload);
};

/** p_med_consultation_approved — fires on successful submission */
export const trackPMedConsultationApproved = (payload: {
  submission_id: string;
}): void => {
  posthogCapture('p_med_consultation_approved', payload);
  brazeCapture('p_med_consultation_approved', payload);
};

/** p_med_consultation_abandoned — fires when the user closes mid-flow */
export const trackPMedConsultationAbandoned = (payload: {
  last_question_index: number;
  phase: string;
}): void => {
  posthogCapture('p_med_consultation_abandoned', payload);
};

/** p_med_consultation_cart_added — fires after approved + addToCart success */
export const trackPMedConsultationCartAdded = (payload: {
  submission_id: string;
  variant_id: string;
}): void => {
  posthogCapture('p_med_consultation_cart_added', payload);
  brazeCapture('p_med_consultation_cart_added', payload);
};

/**
 * purchase — fires GA4 purchase event + PostHog purchase event +
 * Google Ads conversion (if configured).
 *
 * Also attaches `user_data` for enhanced conversions (GTM hashes automatically).
 * Note: enhanced conversion user data is GA-specific and not sent to PostHog
 * since PostHog already has the user identity via posthog.identify().
 *
 * GA4 dataLayer receives scrubbed items (no product names without analytics
 * consent). PostHog and Braze receive full items and self-gate on their own
 * consent.
 */
export const trackPurchase = (
  transactionId: string,
  value: number,
  currency: string,
  tax: number,
  shipping: number,
  items: GA4Item[],
  userData?: EnhancedConversionsUserData,
): void => {
  const params = {
    transaction_id: transactionId,
    value,
    currency,
    tax,
    shipping,
    items,
  };

  const dataLayerParams = { ...params, items: scrubItemsForDataLayer(items) };

  // GA4 purchase event onto the dataLayer. GTM's GA4 + Google Ads conversion +
  // enhanced-conversions tags fire off this event server-side; Consent Mode
  // handles suppression if analytics/ads consent is denied.
  //
  // Enhanced-conversions `user_data` is only attached when the user has granted
  // advertising consent (no PII enters the dataLayer otherwise). GTM hashes it
  // before sending. PostHog/Braze never receive user_data — they already hold
  // the user identity via identify().
  const consent = getConsentPreferences();
  const includeUserData = Boolean(
    consent?.advertising && userData && hasUserData(userData),
  );

  if (includeUserData) {
    pushEcommerce('purchase', dataLayerParams, { user_data: userData });
  } else {
    pushEcommerce('purchase', dataLayerParams);
  }

  posthogCapture('purchase', params);
  brazeCapture('purchase', params);
};

/**
 * Newsletter signup source — distinguishes which surface the user came
 * from so the marketing team can build per-source canvases and segments.
 */
export type NewsletterSignupSource =
  | 'homepage'
  | 'footer'
  | 'registration'
  | 'checkout';

/**
 * newsletter_signup — fired when a user submits a newsletter signup
 * intent (homepage form, registration checkbox, or checkout checkbox).
 *
 * GDPR / PECR posture:
 * - Raw email is NEVER sent to GA4 or PostHog (PII).
 * - The email IS captured in the Braze custom event because the Braze
 *   wrapper is already gated on advertising consent + the user has just
 *   given explicit consent for marketing communication by ticking the
 *   box. The downstream server-side flow (newsletter.subscribed event
 *   in newt-medusa) is the authoritative trigger for the double-opt-in
 *   canvas — this client-side event is purely for funnel telemetry +
 *   real-time canvas triggering when the user is already identified.
 *
 * @param source where the signup originated
 * @param email  user-supplied email; included in Braze payload only
 */
export const trackNewsletterSignup = (
  source: NewsletterSignupSource,
  email: string,
): void => {
  // GA + PostHog get source only — no email (PII).
  const analyticsParams = { source };
  pushDataLayer('newsletter_signup', analyticsParams);
  posthogCapture('newsletter_signup', analyticsParams);

  // Braze gets source + email so canvases can match on the email alias.
  // Wrapper is no-op-safe and consent-gated.
  brazeCapture('newsletter_signup', { source, email });
};

/**
 * account_email_change_intent — fired when a user clicks the "Coming soon"
 * email-change placeholder in account settings.
 *
 * Used to gauge demand for the email-change feature before it is built.
 * No PII is sent — the user is already identified via posthogIdentify.
 */
export const trackEmailChangeIntent = (): void => {
  posthogCapture('account_email_change_intent');
};

/**
 * pdp_unbuyable_variant_viewed — data-hygiene signal fired when a shopper
 * lands on a variant whose PDP is live but whose CTA is hard-disabled because
 * of its own metadata (not_for_sale, or missing classification/questionnaire).
 *
 * The `missing-questionnaire` reason is the one worth watching: a P/POM
 * product carrying a classification but no questionnaire_id is meant to be
 * sold via consultation and silently can't be — a likely lost sale rather
 * than a deliberate block. PostHog-only; no PII, product identifiers only.
 */
export const trackUnbuyableVariantViewed = (payload: {
  product_handle: string;
  variant_id: string;
  variant_sku?: string;
  reason: 'not-for-sale' | 'missing-questionnaire' | 'missing-classification';
  legal_classification?: string;
}): void => {
  posthogCapture('pdp_unbuyable_variant_viewed', payload);
};
